// Profile.js
// Mirrors ProfileView.swift + ProfileComponents.swift exactly.
//
// Sections (top → bottom):
//   Username header (italic, yellow underline)
//   LevelXPBar (hex badge + progress bar)
//   Sport filter pills (5 sports)
//   ProfileStatsDashboard (total drafts, top draft, top streak)
//   SportSpecificStats (per-sport collapsible: leaderboard, roster battles,
//                       gauntlet, recon, stat attack)
//   Badges bar (collapsible, count pill)
//   Log Out button

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import {
  loadProfileStats,
  badgeColorForLevel,
} from "../managers/profileService";
import { computeAllBadges, BADGE_CATEGORIES } from "../managers/badgeLogic";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import rankingIcon from "../assets/ranking-fill.svg";
import shieldIcon from "../assets/shield-fill.svg";
import medalIcon from "../assets/medal-fill.svg";

const APP_THEME = "#d4f53c";
const SURFACE          = "rgba(255,255,255,0.05)";
const SURFACE_BORDER   = "rgba(255,255,255,0.09)";
const SURFACE_SECONDARY= "rgba(255,255,255,0.03)";

const SPORTS = [
  { id: "football",   name: "Football",   hex: "#00b04f", icon: footballIcon },
  { id: "baseball",   name: "Baseball",   hex: "#db2626", icon: baseballIcon },
  { id: "basketball", name: "Basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey",     name: "Hockey",     hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer",     name: "Soccer",     hex: "#1a66e6", icon: soccerIcon },
];

// ─── Main Profile Screen ──────────────────────────────────────────────────────

export default function Profile() {
  const navigate = useNavigate();
  const { user, isGuest, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [rawStats, setRawStats] = useState(null);
  const [hasAppeared, setHasAppeared] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(SPORTS[0]);

  useEffect(() => {
    setTimeout(() => setHasAppeared(true), 150);
  }, []);

  useEffect(() => {
    if (!user) { setIsLoading(false); return; }
    loadProfileStats(user.uid).then(({ profile, raw }) => {
      setStats(profile);
      setRawStats(raw);
      setIsLoading(false);
    });
  }, [user]);

  function handleLogout() { logout(); navigate("/login"); }

  if (isGuest) return <GuestProfile onSignIn={() => navigate("/login")} />;

  return (
    <div style={s.page}>
      {/* App theme ambient glow */}
      <div style={s.glow} />

      <div style={s.container}>
        <div style={{ paddingTop: "12px" }} />

        {isLoading ? <LoadingState /> : (
          <>
            {/* ── Username ── */}
            <div style={{
              ...s.fade,
              opacity: hasAppeared ? 1 : 0,
              transform: hasAppeared ? "none" : "translateY(8px)",
              transitionDelay: "0.15s",
            }}>
              <p style={s.username}>{user?.username || "—"}</p>
              <div style={s.usernameRule} />
              <p style={s.usernameNote}>
                Only data recorded in iOS app version is saved to profile permanently
              </p>
            </div>

            {/* ── Level XP Bar ── */}
            {stats && (
              <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.25s" }}>
                <LevelXPBar stats={stats} />
              </div>
            )}

            {/* ── Sport filter pills ── */}
            <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.3s" }}>
              <SportFilterRow selectedSport={selectedSport} onSelect={setSelectedSport} />
            </div>

            {/* ── Total stats dashboard ── */}
            {stats && (
              <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.35s" }}>
                <StatsDashboard stats={stats} />
              </div>
            )}

            {/* ── Sport-specific stats ── */}
            {stats && (
              <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.4s" }}>
                <SportSpecificStats sport={selectedSport} stats={stats.sportStats[selectedSport.id]} />
              </div>
            )}

            {/* ── Badges bar ── */}
            {stats && rawStats && (
              <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.5s" }}>
                <BadgesBar rawStats={rawStats} />
              </div>
            )}

            {/* ── Log out ── */}
            <div style={{ ...s.fade, opacity: hasAppeared ? 1 : 0, transitionDelay: "0.55s" }}>
              <LogoutSection onLogout={handleLogout} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Level XP Bar ─────────────────────────────────────────────────────────────
// Mirrors LevelXPBar.swift

function LevelXPBar({ stats }) {
  const { level, totalXP, progressFraction, xpToNext } = stats;
  const color = badgeColorForLevel(level);
  const isMax = xpToNext === null;

  return (
    <div style={s.xpCard}>
      {/* Hex badge */}
      <div style={{ ...s.hexBadge, borderColor: color, boxShadow: `0 2px 10px ${color}4d` }}>
        <span style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>
          {level >= 10 ? "★" : level}
        </span>
      </div>

      <div style={s.xpBarContent}>
        {/* Progress bar */}
        <div style={s.xpTrack}>
          <div style={{
            ...s.xpFill,
            width: `${Math.max(progressFraction * 100, 1.5)}%`,
            background: `linear-gradient(to right, ${APP_THEME}, ${APP_THEME}aa)`,
            boxShadow: `0 0 6px ${APP_THEME}66`,
            transition: "width 0.6s ease 0.3s",
          }} />
        </div>
        {/* Labels */}
        <div style={s.xpLabels}>
          <span style={s.xpValue}>{totalXP.toLocaleString()} XP</span>
          <span style={s.xpNext}>
            {isMax ? "MAX LEVEL" : `${xpToNext?.toLocaleString()} XP to Level ${level + 1}`}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Sport filter row ─────────────────────────────────────────────────────────
// Mirrors SportFilterButton.swift

function SportFilterRow({ selectedSport, onSelect }) {
  return (
    <div style={s.sportRow}>
      {SPORTS.map(sport => {
        const isSelected = sport.id === selectedSport.id;
        return (
          <button
            key={sport.id}
            style={{
              ...s.sportPill,
              background: isSelected ? sport.hex : SURFACE,
              border: `1px solid ${isSelected ? "transparent" : SURFACE_BORDER}`,
              boxShadow: isSelected ? `0 4px 12px ${sport.hex}4d` : "none",
            }}
            onClick={() => onSelect(sport)}
          >
            <img
              src={sport.icon}
              alt={sport.name}
              style={{
                width: 24,
                height: 24,
                // Black on the colored pill when selected; white/dimmed on the
                // dark surface otherwise (matches the leaderboard sport filter).
                filter: isSelected ? "brightness(0)" : "brightness(0) invert(1)",
                opacity: isSelected ? 1 : 0.55,
              }}
            />
          </button>
        );
      })}
    </div>
  );
}

// ─── Stats Dashboard ──────────────────────────────────────────────────────────
// Mirrors ProfileStatsDashboard.swift

function StatsDashboard({ stats }) {
  const topDraft = stats.topDraftAllTime ||
    Math.max(0, ...Object.values(stats.sportStats).map(ss => ss.highScore));
  const topStreak = stats.bestStreak ||
    Math.max(0, ...Object.values(stats.sportStats).map(ss => ss.highStreak));

  return (
    <div style={{
      ...s.card,
      background: `${APP_THEME}0a`,
      border: `1px solid ${APP_THEME}2e`,
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
    }}>
      <DashStat value={stats.totalDrafts} label="TOTAL DRAFTS" />
      <div style={s.pillDivider} />
      <DashStat value={topDraft}          label="TOP DRAFT"    />
      <div style={s.pillDivider} />
      <DashStat value={topStreak}         label="TOP STREAK"   />
    </div>
  );
}

function DashStat({ value, label }) {
  return (
    <div style={s.dashStat}>
      <span style={{ ...s.dashValue, color: APP_THEME }}>{value > 0 ? value : "—"}</span>
      <span style={s.dashLabel}>{label}</span>
    </div>
  );
}

// ─── Sport-specific stats ─────────────────────────────────────────────────────
// Mirrors SportSpecificStats.swift — collapsible sections

function SportSpecificStats({ sport, stats }) {
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBattles, setShowBattles] = useState(false);
  const [showGauntlet, setShowGauntlet] = useState(false);
  const [showRecon, setShowRecon] = useState(false);
  const [showStatAttack, setShowStatAttack] = useState(false);

  if (!stats) return null;

  return (
    <div style={{
      ...s.sportStatsCard,
      background: `${sport.hex}0a`,
      border: `1px solid ${sport.hex}38`,
    }}>
      {/* Top 4-column row */}
      <div style={s.sportStatsRow}>
        <SportStat value={stats.draftsCompleted} label={`${sport.name.toUpperCase()}\nDRAFTS`} sport={sport} />
        <div style={s.pillDivider} />
        <SportStat value={stats.highScore}        label={"TOP\nDRAFT"} sport={sport} />
        <div style={s.pillDivider} />
        <SportStat value={stats.highStreak}       label={"TOP\nSTREAK"} sport={sport} />
        <div style={s.pillDivider} />
        <SportStat value={stats.dynastyRating}    label={"DYNASTY\nRATING"} sport={sport} />
      </div>

      {/* Leaderboard */}
      <CollapsibleSection
        label="LEADERBOARD" iconSrc={rankingIcon}
        sport={sport} isOpen={showLeaderboard}
        onToggle={() => setShowLeaderboard(v => !v)}
      >
        <RankRow label="DRAFTS BEST DAILY FINISH"    rank={stats.bestDailyDraft}    sport={sport} />
        <RankRow label="DRAFTS BEST MONTHLY FINISH"  rank={stats.bestMonthlyDraft}  sport={sport} />
        <CountRow label="DRAFTS DAILY CHAMPION WINS" count={stats.champDraft}       sport={sport} />
        <RankRow label="STREAKS BEST DAILY FINISH"   rank={stats.bestDailyStreak}   sport={sport} />
        <RankRow label="STREAKS BEST MONTHLY FINISH" rank={stats.bestMonthlyStreak} sport={sport} />
        <CountRow label="STREAKS DAILY CHAMPION WINS"count={stats.champStreak}      sport={sport} />
      </CollapsibleSection>

      {/* Roster Battles */}
      <CollapsibleSection
        label="ROSTER BATTLES" iconSrc={shieldIcon}
        sport={sport} isOpen={showBattles}
        onToggle={() => setShowBattles(v => !v)}
      >
        <RecordRow versusRecord={stats.versusRecord} sport={sport} />
      </CollapsibleSection>

      {/* Gauntlet */}
      <CollapsibleSection
        label="GAUNTLET" icon="⚔️"
        sport={sport} isOpen={showGauntlet}
        onToggle={() => setShowGauntlet(v => !v)}
      >
        <ComingSoonRow label="Stats coming when Gauntlet is built for web" />
      </CollapsibleSection>

      {/* Roster Recon */}
      <CollapsibleSection
        label="ROSTER RECON" icon="🔍"
        sport={sport} isOpen={showRecon}
        onToggle={() => setShowRecon(v => !v)}
      >
        <ComingSoonRow label="Stats coming when Recon is built for web" />
      </CollapsibleSection>

      {/* Stat Attack */}
      <CollapsibleSection
        label="STAT ATTACK" icon="📊"
        sport={sport} isOpen={showStatAttack}
        onToggle={() => setShowStatAttack(v => !v)}
      >
        <ComingSoonRow label="Stats coming when Stat Attack is built for web" />
      </CollapsibleSection>
    </div>
  );
}

function SportStat({ value, label, sport }) {
  const lines = label.split("\n");
  return (
    <div style={s.dashStat}>
      <span style={{ ...s.dashValue, fontSize: 22, color: sport.hex }}>{value > 0 ? value : "—"}</span>
      {lines.map((l, i) => (
        <span key={i} style={{ ...s.dashLabel, lineHeight: 1.1 }}>{l}</span>
      ))}
    </div>
  );
}

function CollapsibleSection({ label, icon, iconSrc, sport, isOpen, onToggle, children }) {
  return (
    <>
      <div style={{ ...s.sectionRule, background: `${sport.hex}26` }} />
      <button style={s.sectionHeader} onClick={onToggle}>
        {iconSrc ? (
          <span style={{
            display: "inline-block",
            width: 15,
            height: 15,
            background: sport.hex,
            WebkitMaskImage: `url(${iconSrc})`,
            maskImage: `url(${iconSrc})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }} />
        ) : (
          <span style={{ fontSize: 14 }}>{icon}</span>
        )}
        <span style={s.sectionLabel}>{label}</span>
        <span style={s.chevron}>{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen && (
        <div style={s.sectionBody}>
          {children}
        </div>
      )}
    </>
  );
}

function RankRow({ label, rank, sport }) {
  const hasRank = rank && rank <= 100;
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      <span style={{ ...s.detailValue, color: hasRank ? sport.hex : "rgba(255,255,255,0.2)" }}>
        {hasRank ? `#${rank}` : "—"}
      </span>
    </div>
  );
}

function CountRow({ label, count, sport }) {
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      <span style={{ ...s.detailValue, color: count > 0 ? sport.hex : "rgba(255,255,255,0.2)" }}>
        {count > 0 ? count : "—"}
      </span>
    </div>
  );
}

function RecordRow({ versusRecord, sport }) {
  const isEmpty = !versusRecord || versusRecord === "0-0" || versusRecord === "";
  const parts = versusRecord?.split("-") || [];
  const wins = parseInt(parts[0]) || 0;
  const losses = parseInt(parts[1]) || 0;
  const pct = wins + losses > 0 ? `${Math.round(wins / (wins + losses) * 100)}%` : "—";

  return (
    <div style={{ ...s.sportStatsRow, padding: "10px 0" }}>
      <div style={s.dashStat}>
        <span style={{ ...s.dashValue, fontSize: 22, color: sport.hex }}>
          {isEmpty ? "—" : versusRecord}
        </span>
        <span style={s.dashLabel}>RECORD</span>
      </div>
      <div style={s.pillDivider} />
      <div style={s.dashStat}>
        <span style={{ ...s.dashValue, fontSize: 22, color: sport.hex }}>
          {isEmpty ? "—" : pct}
        </span>
        <span style={s.dashLabel}>WIN %</span>
      </div>
    </div>
  );
}

function ComingSoonRow({ label }) {
  return (
    <div style={{ padding: "10px 16px" }}>
      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>
        {label}
      </span>
    </div>
  );
}

// ─── Badges bar ───────────────────────────────────────────────────────────────
// Mirrors badgesBar in ProfileView.swift — collapsible, categorised

function BadgesBar({ rawStats }) {
  const [isOpen, setIsOpen] = useState(false);
  const allBadges = computeAllBadges(rawStats);
  const unlocked  = allBadges.filter(b => b.isUnlocked).length;
  const total     = allBadges.length;

  // Group by category in definition order
  const byCategory = {};
  BADGE_CATEGORIES.forEach(cat => { byCategory[cat.id] = []; });
  allBadges.forEach(b => {
    if (byCategory[b.category]) byCategory[b.category].push(b);
  });

  return (
    <div style={s.card}>
      {/* Header */}
      <button style={s.badgesHeader} onClick={() => setIsOpen(v => !v)}>
        <div style={s.badgesHeaderLeft}>
          <span style={{
            display: "inline-block",
            width: 16,
            height: 16,
            background: APP_THEME,
            WebkitMaskImage: `url(${medalIcon})`,
            maskImage: `url(${medalIcon})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }} />
          <span style={s.badgesTitle}>BADGES</span>
          <span style={s.badgesCount}>{unlocked} / {total}</span>
        </div>
        <span style={s.chevron}>{isOpen ? "▲" : "▼"}</span>
      </button>

      {/* Collapsible badge list */}
      {isOpen && (
        <div style={{ paddingBottom: 12 }}>
          {BADGE_CATEGORIES.map(cat => {
            const badges = byCategory[cat.id] || [];
            if (!badges.length) return null;
            const catUnlocked = badges.filter(b => b.isUnlocked).length;
            return (
              <div key={cat.id}>
                {/* Category header */}
                <div style={s.catHeader}>
                  <span style={s.catLabel}>{cat.label}</span>
                  <span style={s.catCount}>{catUnlocked}/{badges.length}</span>
                </div>
                {/* Badge rows */}
                {badges.map(badge => (
                  <BadgeRow key={badge.badgeID} badge={badge} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BadgeRow({ badge }) {
  return (
    <div style={{
      ...s.badgeRow,
      background: badge.isUnlocked ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
      borderColor: badge.isUnlocked ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.05)",
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          ...s.badgeTitle,
          color: badge.isUnlocked ? APP_THEME : "rgba(255,255,255,0.5)",
        }}>
          {badge.title}
        </p>
        <p style={s.badgeDesc}>{badge.description}</p>
      </div>
      <div style={s.badgeRight}>
        {badge.isUnlocked
          ? <span style={{ fontSize: 16, color: APP_THEME }}>✓</span>
          : <span style={s.badgeXP}>{badge.xpValue.toLocaleString()} XP</span>
        }
      </div>
    </div>
  );
}

function LogoutSection({ onLogout }) {
  return (
    <div style={s.card}>
      <button style={s.logoutBtn} onClick={onLogout}>
        <span style={s.logoutText}>LOG OUT</span>
      </button>
    </div>
  );
}

// ─── Guest profile ────────────────────────────────────────────────────────────

function GuestProfile({ onSignIn }) {
  return (
    <div style={s.guestContainer}>
      <span style={{ fontSize: 48 }}>👤</span>
      <p style={s.guestTitle}>You're playing as a guest</p>
      <p style={s.guestSub}>
        Create a free account to save your scores, track your progress, and compete on the global leaderboard.
      </p>
      <button style={s.signInBtn} onClick={onSignIn}>CREATE ACCOUNT / SIGN IN</button>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
      <span style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>Loading profile...</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: { minHeight: "100vh", position: "relative" },
  glow: {
    position: "fixed",
    top: 0, left: 0, right: 0, height: "40vh",
    background: `radial-gradient(ellipse at top left, ${APP_THEME}12, transparent 60%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  container: {
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 120px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    position: "relative",
    zIndex: 1,
  },
  fade: {
    transition: "opacity 0.4s ease, transform 0.4s ease",
  },

  // Username
  username: {
    fontSize: "30px",
    fontWeight: "900",
    fontStyle: "italic",
    color: "#ffffff",
    letterSpacing: "0.5px",
    textAlign: "center",
    margin: 0,
    lineHeight: 1.1,
  },
  usernameRule: {
    height: "1px",
    width: "60%",
    margin: "6px auto 0",
    background: `${APP_THEME}99`,
  },
  usernameNote: {
    fontSize: "13px",
    fontWeight: "600",
    fontStyle: "italic",
    color: APP_THEME,
    textAlign: "center",
    margin: "8px auto 0",
    maxWidth: "280px",
    lineHeight: 1.3,
  },

  // XP bar
  xpCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 16px",
    background: SURFACE,
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "14px",
  },
  hexBadge: {
    width: "46px",
    height: "46px",
    borderRadius: "10px",
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: "rgba(255,255,255,0.06)",
  },
  xpBarContent: { flex: 1, display: "flex", flexDirection: "column", gap: "5px" },
  xpTrack: {
    height: "10px",
    borderRadius: "5px",
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  xpFill: { height: "100%", borderRadius: "5px", minWidth: "4px" },
  xpLabels: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  xpValue: { fontSize: "12px", fontWeight: "700", color: "rgba(255,255,255,0.85)" },
  xpNext:  { fontSize: "11px", fontWeight: "500", color: "rgba(255,255,255,0.4)" },

  // Sport filter
  sportRow: { display: "flex", gap: "8px" },
  sportPill: {
    flex: 1,
    height: "50px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    touchAction: "manipulation",
  },

  // Shared card
  card: {
    background: SURFACE,
    border: `1px solid ${SURFACE_BORDER}`,
    borderRadius: "14px",
    overflow: "hidden",
  },

  // Stats dashboard
  dashStat: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    padding: "8px 0",
  },
  dashValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#ffffff",
    lineHeight: 1,
  },
  dashLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.45)",
    letterSpacing: "1px",
    textAlign: "center",
    lineHeight: 1.2,
  },
  pillDivider: {
    width: "1px",
    height: "36px",
    background: "rgba(255,255,255,0.08)",
    alignSelf: "center",
    flexShrink: 0,
  },

  // Sport-specific stats
  sportStatsCard: {
    borderRadius: "14px",
    overflow: "hidden",
  },
  sportStatsRow: {
    display: "flex",
    alignItems: "stretch",
    padding: "0 0 0 0",
  },
  sectionRule: {
    height: "1px",
    margin: "0 16px",
  },
  sectionHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    touchAction: "manipulation",
  },
  sectionLabel: {
    flex: 1,
    fontSize: "13px",
    fontWeight: "700",
    color: "#ffffff",
    letterSpacing: "0.5px",
  },
  chevron: {
    fontSize: "10px",
    color: "rgba(255,255,255,0.5)",
    fontWeight: "700",
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
  },
  detailLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.3px",
  },
  detailValue: {
    fontSize: "14px",
    fontWeight: "700",
  },

  // Badges
  badgesHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "none",
    border: "none",
    cursor: "pointer",
    touchAction: "manipulation",
  },
  badgesHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  badgesTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#fff",
    letterSpacing: "0.5px",
  },
  badgesCount: {
    fontSize: "16px",
    fontWeight: "700",
    color: APP_THEME,
    padding: "3px 10px",
    background: `${APP_THEME}26`,
    borderRadius: "6px",
  },
  badgesBody: {
    padding: "8px 16px 14px",
  },
  badgesComingSoon: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.35)",
    fontStyle: "italic",
    margin: 0,
    lineHeight: 1.5,
  },

  // Badge rows
  catHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px 4px",
  },
  catLabel: {
    fontSize: "10px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.35)",
    letterSpacing: "1.2px",
  },
  catCount: {
    fontSize: "10px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.25)",
  },
  badgeRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 12px",
    margin: "2px 8px",
    borderRadius: "8px",
    border: "1px solid",
  },
  badgeTitle: {
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "0.3px",
    margin: 0,
    lineHeight: 1.2,
  },
  badgeDesc: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.55)",
    margin: "2px 0 0",
    lineHeight: 1.3,
  },
  badgeRight: {
    flexShrink: 0,
    paddingTop: "2px",
  },
  badgeXP: {
    fontSize: "11px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.3px",
  },

  // Logout
  logoutBtn: {
    width: "100%",
    padding: "14px",
    background: "none",
    border: "none",
    cursor: "pointer",
    touchAction: "manipulation",
  },
  logoutText: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#f25c5c",
    letterSpacing: "1px",
    display: "block",
    textAlign: "center",
  },

  // Guest
  guestContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    padding: "60px 32px 40px",
    textAlign: "center",
  },
  guestTitle: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#fff",
    margin: 0,
  },
  guestSub: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.6,
    maxWidth: "280px",
    margin: 0,
  },
  signInBtn: {
    marginTop: "8px",
    padding: "14px 24px",
    borderRadius: "12px",
    background: APP_THEME,
    border: "none",
    fontSize: "14px",
    fontWeight: "800",
    color: "#000",
    letterSpacing: "1px",
    cursor: "pointer",
  },
};
